import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../lib/cn';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /** Optional leading icon (already sized). */
  icon?: React.ReactNode;
  block?: boolean;
}

const base =
  'relative inline-flex items-center justify-center gap-2 font-medium select-none ' +
  'rounded-[var(--radius-md)] transition-[transform,box-shadow,background-color,color] ' +
  'duration-150 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-55 ' +
  'disabled:active:translate-y-0 whitespace-nowrap';

const variants: Record<Variant, string> = {
  // Tactile: subtle top sheen + pressed shadow.
  primary:
    'bg-accent text-accent-contrast shadow-[var(--shadow-card)] ' +
    'hover:bg-accent-strong hover:shadow-[var(--shadow-raised)]',
  secondary:
    'bg-paper-raised text-ink border border-edge-strong shadow-[var(--shadow-card)] ' +
    'hover:border-accent hover:text-accent',
  ghost: 'bg-transparent text-ink-soft hover:bg-paper-sunken hover:text-ink',
  danger: 'bg-[var(--color-wrong)] text-white shadow-[var(--shadow-card)] hover:brightness-95',
};

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-[0.8125rem]',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    icon,
    block,
    className,
    children,
    disabled,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(base, variants[variant], sizes[size], block && 'w-full', className)}
      {...rest}
    >
      {loading && <Spinner size={16} label="Working" />}
      {!loading && icon}
      {children}
    </button>
  );
});
